/**
 * PlagueVectorSkillEffect — Poison tree plague path active: carrier marking.
 *
 * Marks a single enemy as a carrier. While marked, every `poison` DoT tick
 * the carrier receives causes 60% of the tick damage to jump to the nearest
 * non-poisoned enemy within 3 tiles. One hop per tick, single target, via
 * ChainTargetingSystem in 'single' mode.
 *
 * Mark lifecycle: the carrier_mark is permanent (-1 duration) and persists
 * until the carrier's poison expires. The skill tracks this via
 * `notifyPoisonExpired()` called externally when the marked target loses
 * its poison DoT.
 *
 * Upgrade keys via resolver:
 * - Jump Vector: `jumpRangeTiles` (additive) — +1 tile range (3 → 4)
 * - Virulence: `jumpTransferPercent` (max) — override 0.6 → 0.8
 * - Vectors (tree passive): `jumpBonusDotChance` (max, tree scope) — 15% chance
 *   per hop to apply a bonus independent 2-second poison DoT at flat intensity 3
 *   (bypasses Toxicologist multiplier). `jumpBonusDotDurationMs` (max, tree scope).
 *
 * Contagion evolution replaces this skill on unlock.
 *
 * Implements SkillEffect directly for full cooldown and mark lifecycle
 * control. Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Plague Vector
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type {
  ChainConfig,
  ChainTarget,
  ChainTargetResult,
} from '../../../systems/ChainTargetingSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PlagueVectorDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs?: number;
      intensity?: number;
    }): void;
    remove(defId: string): void;
  };
  readonly chainTargeting: {
    findChainTargets(
      config: ChainConfig,
      candidates: Iterable<ChainTarget>,
    ): ChainTargetResult;
  };
  readonly target: {
    getCurrentTargetId(): string;
  };
  readonly rng: {
    roll(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PlagueVectorConfig {
  readonly cooldownMs: number;
  readonly baseJumpRangeTiles: number;
  readonly baseTransferPercent: number;
}

const DEFAULT_CONFIG: PlagueVectorConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  baseJumpRangeTiles: 3,
  baseTransferPercent: 0.6,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Flat intensity for Vectors bonus DoT — bypasses Toxicologist. */
const VECTORS_BONUS_INTENSITY = 3;

/** Record of a single chain jump event for assertions. */
export interface JumpRecord {
  readonly tickDamage: number;
  readonly jumpDamage: number;
  readonly targetId: string;
  readonly rangeTiles: number;
  readonly transferPercent: number;
  /** Whether the Vectors bonus DoT was applied on this hop. */
  readonly bonusDotApplied?: boolean;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PlagueVectorSkillEffect implements SkillEffect {
  readonly skillId = 'plague_vector';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: PlagueVectorDeps;
  private readonly config: PlagueVectorConfig;

  private _cooldownRemaining = 0;
  private _activationCount = 0;

  /** Active mark state. */
  private _markActive = false;
  private _markTargetId: string | null = null;

  /** Jump tracking. */
  private _jumpLog: JumpRecord[] = [];
  private _wastedJumps = 0;
  private _lastChainConfig: ChainConfig | null = null;

  constructor(deps: PlagueVectorDeps, config: Partial<PlagueVectorConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get markActive(): boolean { return this._markActive; }
  get markTargetId(): string | null { return this._markTargetId; }
  get jumpLog(): readonly JumpRecord[] { return this._jumpLog; }
  get wastedJumps(): number { return this._wastedJumps; }
  get lastChainConfig(): ChainConfig | null { return this._lastChainConfig; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const targetId = this.deps.target.getCurrentTargetId();

    // Apply carrier_mark to the target (permanent — expires when poison does)
    // STUB: deferred — no plague spread system reads this mark
    this.deps.statusEffects.apply('carrier_mark', {
      sourceSkillId: 'plague_vector',
      durationMs: -1,
    });

    this._markActive = true;
    this._markTargetId = targetId;
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  /**
   * Called externally when the marked target receives a `poison` DoT tick.
   * Fires a chain jump to the nearest valid enemy within range.
   *
   * @param tickDamage - The poison tick damage dealt to the carrier
   * @param markedTargetX - X position of the carrier-marked enemy (chain origin)
   * @param markedTargetY - Y position of the carrier-marked enemy
   * @param candidates - Nearby enemies eligible to receive the jump
   * @returns The jump record if a valid target was found, null if wasted
   */
  notifyPoisonTick(
    tickDamage: number,
    markedTargetX: number,
    markedTargetY: number,
    candidates: ChainTarget[],
  ): JumpRecord | null {
    if (!this._markActive) return null;

    const rangeTiles = this.resolveJumpRange();
    const transferPercent = this.resolveTransferPercent();

    const chainConfig: ChainConfig = {
      originX: markedTargetX,
      originY: markedTargetY,
      maxTargets: 1,
      maxRangeTiles: rangeTiles,
      damageRetention: transferPercent,
      mode: 'single',
      sourceEntityId: this._markTargetId ?? undefined,
      excludeStatus: 'carrier_mark',
    };

    this._lastChainConfig = chainConfig;

    const result = this.deps.chainTargeting.findChainTargets(chainConfig, candidates);

    if (result.targets.length === 0) {
      this._wastedJumps++;
      return null;
    }

    const hit = result.targets[0];
    const jumpDamage = tickDamage * hit.damageMultiplier;

    // Vectors bonus DoT: independent roll per hop
    const bonusDotApplied = this.rollVectorsBonusDot();

    const record: JumpRecord = {
      tickDamage,
      jumpDamage,
      targetId: hit.target.id,
      rangeTiles,
      transferPercent,
      bonusDotApplied,
    };

    this._jumpLog.push(record);
    return record;
  }

  /**
   * Called when the marked target's `poison` DoT expires. Clears the
   * carrier_mark — the carrier is no longer infectious.
   */
  notifyPoisonExpired(): void {
    if (!this._markActive) return;

    this.deps.statusEffects.remove('carrier_mark');
    this._markActive = false;
    this._markTargetId = null;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    if (this._markActive) {
      this.deps.statusEffects.remove('carrier_mark');
    }
    this._markActive = false;
    this._markTargetId = null;
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private resolveJumpRange(): number {
    const base = this.config.baseJumpRangeTiles;
    const bonus = this.deps.resolver.getAdditiveEffect('plague_vector', 'jumpRangeTiles');
    return base + bonus;
  }

  private resolveTransferPercent(): number {
    const override = this.deps.resolver.getMaxEffect('plague_vector', 'jumpTransferPercent');
    return override ?? this.config.baseTransferPercent;
  }

  /**
   * Roll for Vectors bonus DoT on a hop target. If the tree passive
   * `jumpBonusDotChance` is active and the roll succeeds, applies a
   * flat-intensity independent poison with sourceSkillId 'vectors_bonus'.
   * Intensity is VECTORS_BONUS_INTENSITY (3) — bypasses Toxicologist.
   *
   * @returns true if the bonus DoT was applied
   */
  private rollVectorsBonusDot(): boolean {
    const chance = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotChance', 'max',
    );
    if (chance <= 0) return false;

    if (this.deps.rng.roll() >= chance) return false;

    const durationMs = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotDurationMs', 'max',
    );

    this.deps.statusEffects.apply('poison', {
      sourceSkillId: 'vectors_bonus',
      intensity: VECTORS_BONUS_INTENSITY,
      durationMs: durationMs > 0 ? durationMs : 2000,
    });

    return true;
  }
}
